import requestApi, { HTTP_METHOD } from '@/utils/axios.http';
import { AxiosResponse } from 'axios';
import { DetectionResponse } from '@/api/entities/detect';
import {
  FetchResponse,
  PageRequest,
  Processing,
} from '@/api/entities/processing.ts';
import { FirestoreImage } from '../entities/ia';
import { TOKEN_API_PUBLIC } from '@/utils/constant.utils';

const baseURL = import.meta.env.VITE_BASE_API_URL_IA;

const IA_CAR_URI = '/car';

export const createManyProcessings = async (data: Processing[]) => {
  return requestApi({
    url: `${IA_CAR_URI}/processing/createMany`,
    method: HTTP_METHOD.POST,
    body: data,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getAllProcessingsByElementClassId = async (
  relatedTo: 'detection' | 'moderation',
  classId: number,
  page: number,
  limit: number,
) => {
  let url = `${IA_CAR_URI}/processing/allByElementClassId`;
  const queryParams = new URLSearchParams();

  queryParams.append('relatedTo', relatedTo.toString());
  queryParams.append('classId', classId.toString());
  queryParams.append('page', page.toString());
  queryParams.append('limit', limit.toString());

  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const deleteProcessing = async (id: string) => {
  return requestApi({
    url: `${IA_CAR_URI}/processing/${id}`,
    method: HTTP_METHOD.DELETE,
    body: {},
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const detectElement = (
  file: File,
): Promise<DetectionResponse | AxiosResponse> => {
  return requestApi({
    url: `${IA_CAR_URI}/detect-element`,
    method: HTTP_METHOD.POST,
    body: { file },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const detectDamage = (
  file: File,
): Promise<DetectionResponse | AxiosResponse> => {
  return requestApi({
    url: `${IA_CAR_URI}/detect-damage`,
    method: HTTP_METHOD.POST,
    body: { file },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getFileProcessed = (
  fileName: string,
): Promise<DetectionResponse | AxiosResponse> => {
  return requestApi({
    url: `${baseURL}/downloadFile/${fileName}`,
    method: HTTP_METHOD.GET,
    responseType: 'blob',
  });
};

export const removeBackground = (
  file: File,
): Promise<DetectionResponse | AxiosResponse> => {
  return requestApi({
    url: `${IA_CAR_URI}/remove-background`,
    method: HTTP_METHOD.POST,
    body: { file },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const detectOrientation = (
  file: File,
): Promise<DetectionResponse | AxiosResponse> => {
  return requestApi({
    url: `${IA_CAR_URI}/detect-orientation`,
    method: HTTP_METHOD.POST,
    body: { file },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const readCarteGrise = (
  file: File,
): Promise<DetectionResponse | AxiosResponse> => {
  return requestApi({
    url: `${IA_CAR_URI}/read-carte-grise`,
    method: HTTP_METHOD.POST,
    body: { file },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getAllProcessing = (
  params: PageRequest,
): Promise<FetchResponse<Processing> | AxiosResponse> => {
  let url = `${IA_CAR_URI}/processing`;
  const queryParams = new URLSearchParams();

  queryParams.append('page', params.page.toString());
  queryParams.append('limit', params.limit.toString());

  if (params.key) {
    queryParams.append('key', params.key);
  }

  if (params.defectKey) {
    queryParams.append('defectKey', params.defectKey);
  }

  if (params.usedStartDate) {
    queryParams.append('usedStartDate', params.usedStartDate);
  }

  if (params.usedEndDate) {
    queryParams.append('usedEndDate', params.usedEndDate);
  }

  if (params.moderatorUserId) {
    queryParams.append('moderatorUserId', params.moderatorUserId);
  }

  if (params.detectionStatus) {
    queryParams.append('detectionStatus', params.detectionStatus);
  }

  if (params.status) {
    queryParams.append('status', params.status);
  }

  if (params.verificationStatus) {
    queryParams.append('verificationStatus', params.verificationStatus);
  }

  if (params.isManualUpload) {
    queryParams.append('isManualUpload', params.isManualUpload);
  }

  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }

  return requestApi({
    url,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getCarProcessedById = (
  id: string,
): Promise<{ data: Processing } | AxiosResponse> => {
  return requestApi({
    url: `car-process/${id}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getNextDataToReport = (
  id: string,
): Promise<{ data: Processing } | AxiosResponse> => {
  return requestApi({
    url: `car-process/data-after-report/${id}`,
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getAllComponentCarKey = async () => {
  return requestApi({
    url: '/getAllComponentKey',
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};

export const getAllDefectCarKey = async () => {
  return requestApi({
    url: '/getAllComponentDefectKey',
    method: HTTP_METHOD.GET,
    headers: {
      'Content-Type': 'application/json',
      'x-api-token': TOKEN_API_PUBLIC,
    },
  });
};

export const updateCarProcessedToModerate = (
  id: string,
  correctionElNb: number,
  correctionDmgNb: number,
  detectionsElements?: object[],
  detectionsDamagesDetected?: object[],
  moderatedClassesElements?: string[],
  moderationsElements?: object[],
  moderatedClassesDamages?: string[],
  moderationsDamages?: object[],
): Promise<AxiosResponse> => {
  return requestApi({
    url: `car/update-correction-processed/${id}`,
    method: HTTP_METHOD.PATCH,
    body: {
      correctionElNb,
      correctionDmgNb,
      detectionsElements,
      detectionsDamagesDetected,
      moderatedClassesElements,
      moderationsElements,
      moderatedClassesDamages,
      moderationsDamages,
    },
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const updateProcessing = (
  processing: Processing,
): Promise<AxiosResponse> => {
  return requestApi({
    url: `${IA_CAR_URI}/processing/update`,
    method: HTTP_METHOD.PATCH,
    body: processing,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const uploadProcessingImage = async (files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  return requestApi({
    url: `${IA_CAR_URI}/processing/uploadImageFile`,
    method: HTTP_METHOD.POST,
    body: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteProcessingImage = async (fileName: string) => {
  return requestApi({
    url: `${IA_CAR_URI}/processing/deleteImageFile/${fileName}`,
    method: HTTP_METHOD.DELETE,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const updateCarSkipped = (id: string): Promise<AxiosResponse> => {
  return requestApi({
    url: `car/update-correction-skipped/${id}`,
    method: HTTP_METHOD.PATCH,
    body: {},
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const createManyFirestoreImages = async (data: FirestoreImage[]) => {
  return requestApi({
    url: 'firestoreImage/createMany',
    method: HTTP_METHOD.POST,
    body: data,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const updateFirestoreImage = (
  firestoreImage: FirestoreImage,
): Promise<AxiosResponse> => {
  return requestApi({
    url: 'firestoreImage/update',
    method: HTTP_METHOD.PATCH,
    body: firestoreImage,
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const deleteFirestoreImageByImageUrl = async (imageUrl: string) => {
  return requestApi({
    url: 'firestoreImage/deleteByImageUrl',
    method: HTTP_METHOD.DELETE,
    body: { imageUrl },
    headers: {
      'Content-Type': 'application/json',
    },
  });
};
